import { nomCompletDe } from '@dinertournant/core';
export class ErreurApi extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly issues: { path: string; message: string }[] = [],
  ) {
    super(message);
    this.name = 'ErreurApi';
  }
}

async function requete<T>(method: string, url: string, body?: unknown): Promise<T> {
  const r = await fetch(url, {
    method,
    headers: body === undefined ? {} : { 'content-type': 'application/json' },
    credentials: 'same-origin',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const texte = await r.text();
  const data = texte ? (JSON.parse(texte) as Record<string, unknown>) : {};
  if (!r.ok) {
    throw new ErreurApi(r.status, typeof data.error === 'string' ? data.error : `Erreur ${r.status}`, (data.issues as ErreurApi['issues']) ?? []);
  }
  return data as T;
}

export const api = {
  get: <T>(url: string) => requete<T>('GET', url),
  post: <T>(url: string, body: unknown) => requete<T>('POST', url, body),
  put: <T>(url: string, body: unknown) => requete<T>('PUT', url, body),
};

export interface EvenementPublic {
  id: string;
  nom: string;
  organisation: string | null;
  description: string | null;
  date: string;
  dateLongue: string;
  zone: string | null;
  horaires: { entree: string; plat: string; dessert: string };
  /** Étape commune facultative de fin de soirée (le lieu reste sur le parcours). */
  commune: { nom: string; heure: string } | null;
  dateLimiteInscription: string;
  inscriptionsOuvertes: boolean;
  complet: boolean;
  placesRestantes: number | null;
}

export interface FoyerPublic {
  id: string;
  nom: string;
  membres: string[];
  /** E-mails alignés sur `membres` (null = non renseigné). */
  emails: (string | null)[];
  email: string | null;
  telephone: string | null;
  adresse: string | null;
  complementAdresse: string | null;
  regimes: string[];
  precisionsRegime: string | null;
  veutRecevoir: boolean;
  disponibilites: ('entree' | 'plat' | 'dessert')[];
  capaciteMax?: number | null;
}

/** « 19:30:00 » → « 19 h 30 » */
export function heureCourte(h: string): string {
  const [hh, mm] = h.split(':');
  return `${Number(hh)} h ${mm}`;
}

/** « 2026-11-07 » → « 7 novembre » */
export function dateCourte(d: string): string {
  const [y, m, j] = d.split('-').map(Number) as [number, number, number];
  return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long' }).format(new Date(y, m - 1, j));
}

/** Première lettre en capitale (« samedi 21 novembre » → « Samedi 21 novembre »). */
export function capitale(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Heure de fin approximative d'une étape : début de la suivante moins 15 min, ou +75 min pour le dessert. */
export function plageEtape(horaires: EvenementPublic['horaires'], etape: 'entree' | 'plat' | 'dessert'): string {
  const debut = horaires[etape];
  const minutes = (h: string) => {
    const [hh, mm] = h.split(':').map(Number) as [number, number];
    return hh * 60 + mm;
  };
  const fmt = (m: number) => `${Math.floor(m / 60) % 24} h ${String(m % 60).padStart(2, '0')}`;
  const fin = etape === 'entree' ? minutes(horaires.plat) - 15 : etape === 'plat' ? minutes(horaires.dessert) - 15 : minutes(horaires.dessert) + 75;
  return `${heureCourte(debut)} – ${fmt(fin)}`;
}

async function requeteSansCorps<T>(method: string, url: string): Promise<T> {
  const r = await fetch(url, { method, credentials: 'same-origin' });
  const texte = await r.text();
  const data = texte ? (JSON.parse(texte) as Record<string, unknown>) : {};
  if (!r.ok) throw new ErreurApi(r.status, typeof data.error === 'string' ? data.error : `Erreur ${r.status}`);
  return data as T;
}
export const apiDel = <T>(url: string) => requeteSansCorps<T>('DELETE', url);

/** Événement vu par l'organisateur (GET /api/events, /api/events/:id). */
export interface Evenement {
  id: string;
  nom: string;
  organisation: string | null;
  description: string | null;
  date: string;
  zone: string | null;
  horaireEntree: string;
  horairePlat: string;
  horaireDessert: string;
  dateLimiteInscription: string;
  capaciteMax: number | null;
  tailleMin: number;
  tailleMax: number;
  slug: string;
  revealAdresseMinAvant: number | null;
  contactNom: string | null;
  contactTelephone: string | null;
  communeActive: boolean;
  communeNom: string | null;
  communeHeure: string | null;
  communeLieu: string | null;
  communeAdresse: string | null;
  communeComplement: string | null;
  repartitionGenereeAt: string | null;
  createdAt: string;
}

export interface EtatPlanning {
  planningObsolete: boolean;
  repartitionGenereeAt: string | null;
  changements: { foyerId: string; nom: string; modifieLe: string }[];
}

export interface DetailEvenement {
  event: Evenement;
  inscriptions: { foyers: number; participants: number; capaciteMax: number | null };
  planning: EtatPlanning;
  liens: { inscription: string };
}

/** « 2026-11-21 » → « samedi 21 novembre » */
export function dateLongue(d: string): string {
  const [y, m, j] = d.split('-').map(Number) as [number, number, number];
  return new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date(y, m - 1, j));
}

/** Nombre de jours entre aujourd'hui et une date AAAA-MM-JJ (négatif si passée). */
export function joursAvant(d: string): number {
  const [y, m, j] = d.split('-').map(Number) as [number, number, number];
  const cible = new Date(y, m - 1, j);
  const now = new Date();
  const aujourdHui = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((cible.getTime() - aujourdHui.getTime()) / 86_400_000);
}

/** « 19:30:00 » → « 19:30 » (valeur d'un <input type="time">). */
export function heureInput(h: string): string {
  return h.slice(0, 5);
}

/** Slug à partir d'un nom : minuscules, sans accents, tirets. */
export function slugDe(nom: string): string {
  return nom
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

/** Foyer vu par l'organisateur (GET /api/events/:id/households). */
export interface FoyerOrganisateur {
  id: string;
  nom: string;
  membres: { id: string; prenomNom: string; participe: boolean; regime?: string | undefined; email?: string | null | undefined }[];
  veutRecevoir: boolean;
  disponibilites: ('entree' | 'plat' | 'dessert')[];
  capaciteMax?: number | undefined;
  adresse?: string | undefined;
  complementAdresse?: string | undefined;
  email: string | null;
  telephone: string | null;
  regimes: string[];
  precisionsRegime: string | null;
  source: string;
  updatedAt: string;
  /** Dernière confirmation d'inscription envoyée (ISO), null si jamais. */
  derniereConfirmation?: string | null;
}

export interface ListeFoyers {
  foyers: FoyerOrganisateur[];
  participants: number;
  capaciteMax: number | null;
  dateLimiteInscription: string;
  planning: EtatPlanning;
}

export async function apiPatch<T>(url: string, body: unknown): Promise<T> {
  const r = await fetch(url, { method: 'PATCH', headers: { 'content-type': 'application/json' }, credentials: 'same-origin', body: JSON.stringify(body) });
  const texte = await r.text();
  const data = texte ? (JSON.parse(texte) as Record<string, unknown>) : {};
  if (!r.ok) throw new ErreurApi(r.status, typeof data.error === 'string' ? data.error : `Erreur ${r.status}`);
  return data as T;
}

/** « Claire et Julien Martin », « Claire, Julien et Léa Martin », « Linh Nguyen et Thomas Roux », « Grace Okafor » (personnes présentes). */
export function nomComplet(f: { nom: string; membres: { prenomNom: string; participe: boolean }[] }): string {
  return nomCompletDe(
    f.membres.filter((m) => m.participe).map((m) => m.prenomNom),
    f.nom,
  );
}
